import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import AppShell from "@/components/AppShell";
import PullToRefresh from "@/components/PullToRefresh";
import { RowListSkeleton } from "@/components/SkeletonList";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import LeaveReviewDialog from "@/components/LeaveReviewDialog";
import RescheduleDialog from "@/components/RescheduleDialog";
import GroomReportDialog from "@/components/GroomReportDialog";
import GroomReportView from "@/components/GroomReportView";
import CompleteBookingDialog from "@/components/CompleteBookingDialog";
import RebookBanner from "@/components/RebookBanner";
import PassportViewDialog from "@/components/PassportViewDialog";
import StarRating from "@/components/StarRating";
import WalkerLiveTracking from "@/components/WalkerLiveTracking";
import BuyerLiveWalkBadge from "@/components/BuyerLiveWalkBadge";
import { GPS_LIVE_WALK_ENABLED } from "@/lib/featureFlags";
import { CalendarIcon as Calendar, ClockIcon as Clock, ShieldCheckIcon as ShieldCheck, CheckCircleIcon as CheckCircle, XCircleIcon as XCircle, WarningIcon as Warning, CurrencyDollarIcon as CurrencyDollar, ReceiptIcon as Receipt, StarIcon as Star, ArrowsClockwiseIcon as ArrowsClockwise, VideoCameraIcon as VideoCamera, ScissorsIcon as Scissors, PawPrintIcon as PawPrint, MapPinIcon as MapPin } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";

function partition(rows) {
  const now = new Date();
  const upcoming = [];
  const past = [];
  rows.forEach((b) => {
    const dt = new Date(b.scheduled_at);
    if (dt >= now && !["cancelled", "completed", "refunded", "no_show"].includes(b.status)) {
      upcoming.push(b);
    } else {
      past.push(b);
    }
  });
  upcoming.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
  past.sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at));
  return { upcoming, past };
}

const STATUS_BADGE = {
  pending_payment: { label: "Awaiting payment", tone: "bg-amber-100 text-amber-800 border-amber-300" },
  scheduled_pending: { label: "Scheduled · charges 24h before", tone: "bg-sky-100 text-sky-900 border-sky-300" },
  payment_failed: { label: "Payment failed", tone: "bg-rose-100 text-rose-800 border-rose-300" },
  confirmed: { label: "Confirmed", tone: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  completed: { label: "Completed", tone: "bg-slate-100 text-slate-700 border-slate-300" },
  cancelled: { label: "Cancelled", tone: "bg-rose-100 text-rose-800 border-rose-300" },
  refunded: { label: "Refunded", tone: "bg-rose-100 text-rose-800 border-rose-300" },
  no_show: { label: "No-show", tone: "bg-rose-100 text-rose-800 border-rose-300" },
};

function prettyDateTime(iso) {
  return new Date(iso).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** `/my-bookings` — customer + provider both land here. */
export default function MyBookings() {
  const [searchParams] = useSearchParams();
  const [role, setRole] = useState("customer");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewBooking, setReviewBooking] = useState(null);
  const [reschedBooking, setReschedBooking] = useState(null);
  const [reportBooking, setReportBooking] = useState(null);
  const [viewReportBooking, setViewReportBooking] = useState(null);
  const [completeBooking, setCompleteBooking] = useState(null);
  const [passportBooking, setPassportBooking] = useState(null);

  const load = async (which) => {
    setLoading(true);
    try {
      const r = await api.get("/bookings/mine", { params: { role: which } });
      setRows(r.data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Stripe success redirect lands here with ?status=success.
    if (searchParams.get("status") === "success") {
      toast.success("Payment received — your booking is confirmed!", { duration: 6000 });
    } else if (searchParams.get("status") === "cancelled") {
      toast.info("Checkout was cancelled. Your booking is still pending payment.");
    }
    load(role);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  const { upcoming, past } = partition(rows);

  const cancel = async (b) => {
    const reason = window.prompt(
      `Cancel this booking? Refund depends on timing:\n` +
        `• 24h+ before → 100%\n• 2-24h → 50%\n• <2h → 0%\n\nOptional reason:`,
    );
    if (reason === null) return;
    try {
      await api.post(`/bookings/${b.id}/cancel`, { reason });
      toast.success("Booking cancelled");
      load(role);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const markComplete = (b) => setCompleteBooking(b);

  const pauseSeries = async (seriesId) => {
    try {
      await api.post(`/bookings/series/${seriesId}/pause`, {});
      toast.success("Series paused. Future sessions won't auto-charge until you resume.");
      load(role);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resumeSeries = async (seriesId) => {
    try {
      await api.post(`/bookings/series/${seriesId}/resume`, {});
      toast.success("Series resumed. Next session will auto-charge 24h before.");
      load(role);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const cancelSeries = async (seriesId) => {
    if (!window.confirm(
      "Cancel the entire recurring series? All future confirmed sessions will be refunded to your card. Past completed sessions are not refunded."
    )) return;
    try {
      const r = await api.post(`/bookings/series/${seriesId}/cancel`, {});
      const refunded = r.data?.report?.refunded?.length || 0;
      const amount = (r.data?.report?.refunded || []).reduce((a, x) => a + (x.amount || 0), 0);
      toast.success(`Series cancelled. ${refunded} session${refunded === 1 ? "" : "s"} refunded ($${amount.toFixed(2)}).`);
      load(role);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <AppShell>
      <PullToRefresh onRefresh={() => load(role)} testId="my-bookings-ptr">
        <div className="mb-6">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B] mb-2">Service bookings</p>
        <h1 className="font-spectral text-4xl sm:text-5xl tracking-tight">My bookings</h1>
        <p className="text-[#6B6B6B] mt-2 max-w-2xl">
          Service bookings you've made or received. Payments are processed by Stripe directly to the provider — Armstrong K9 Registry is the venue.
        </p>
      </div>

      <Tabs value={role} onValueChange={setRole}>
        <TabsList className="mb-4" data-testid="my-bookings-tabs">
          <TabsTrigger value="customer" data-testid="tab-customer">As customer</TabsTrigger>
          <TabsTrigger value="provider" data-testid="tab-provider">As provider</TabsTrigger>
        </TabsList>
        <TabsContent value={role} className="space-y-8">
          {loading ? (
            <RowListSkeleton count={4} testId="my-bookings-skeleton" />
          ) : rows.length === 0 ? (
            <EmptyState role={role} />
          ) : (
            <>
              <Section title="Upcoming" data-testid="bookings-upcoming">
                {upcoming.length === 0 ? (
                  <p className="text-sm text-[#6B6B6B]">Nothing on the calendar.</p>
                ) : (
                  upcoming.map((b, i) => {
                    // Show the series-management bar only on the FIRST
                    // upcoming booking of each series (dedup by series_id).
                    const isFirstOfSeries = b.series_id && upcoming.findIndex((x) => x.series_id === b.series_id) === i;
                    return (
                      <BookingCard
                        key={b.id}
                        b={b}
                        role={role}
                        onCancel={() => cancel(b)}
                        onComplete={() => markComplete(b)}
                        onReschedule={() => setReschedBooking(b)}
                        onReport={role === "provider" && ["confirmed", "completed"].includes(b.status) ? () => setReportBooking(b) : null}
                        onViewReport={role === "customer" && b.groom_report ? () => setViewReportBooking(b) : null}
                        onPassport={role === "provider" && b.dog_id ? () => setPassportBooking(b) : null}
                        showSeriesActions={isFirstOfSeries && role === "customer"}
                        onPauseSeries={() => pauseSeries(b.series_id)}
                        onResumeSeries={() => resumeSeries(b.series_id)}
                        onCancelSeries={() => cancelSeries(b.series_id)}
                      />
                    );
                  })
                )}
              </Section>
              <Section title="History" data-testid="bookings-history">
                {past.length === 0 ? (
                  <p className="text-sm text-[#6B6B6B]">No history yet.</p>
                ) : (
                  past.map((b) => (
                    <BookingCard
                      key={b.id}
                      b={b}
                      role={role}
                      compact
                      onReview={role === "customer" && b.status === "completed" ? () => setReviewBooking(b) : null}
                      onReport={role === "provider" && b.status === "completed" ? () => setReportBooking(b) : null}
                      onViewReport={role === "customer" && b.groom_report ? () => setViewReportBooking(b) : null}
                      onPassport={role === "provider" && b.dog_id ? () => setPassportBooking(b) : null}
                    />
                  ))
                )}
              </Section>
            </>
          )}
        </TabsContent>
      </Tabs>
      <LeaveReviewDialog
        open={!!reviewBooking}
        onOpenChange={(v) => { if (!v) setReviewBooking(null); }}
        booking={reviewBooking}
        onSubmitted={() => load(role)}
      />
      <RescheduleDialog
        open={!!reschedBooking}
        onOpenChange={(v) => { if (!v) setReschedBooking(null); }}
        booking={reschedBooking}
        onRescheduled={() => load(role)}
      />
      <GroomReportDialog
        open={!!reportBooking}
        booking={reportBooking}
        onClose={() => setReportBooking(null)}
        onSent={() => load(role)}
      />
      <GroomReportView
        open={!!viewReportBooking}
        booking={viewReportBooking}
        onClose={() => setViewReportBooking(null)}
      />
      <CompleteBookingDialog
        open={!!completeBooking}
        booking={completeBooking}
        onClose={() => setCompleteBooking(null)}
        onDone={() => load(role)}
      />
      <PassportViewDialog
        open={!!passportBooking}
        dogId={passportBooking?.dog_id}
        booking={passportBooking}
        onClose={() => setPassportBooking(null)}
      />
      </PullToRefresh>
    </AppShell>
  );
}

function EmptyState({ role }) {
  if (role === "provider") {
    return (
      <div className="bg-white border border-[#EAEAEA] rounded-xl p-8 text-center" data-testid="empty-provider">
        <ShieldCheck size={28} weight="duotone" className="text-gold mx-auto mb-3" />
        <h3 className="font-semibold text-ink">No incoming bookings yet</h3>
        <p className="text-sm text-[#6B6B6B] mt-1">
          Make sure your provider profile is approved, your services are published, and your weekly availability is set up.
        </p>
        <Button variant="outline" className="rounded-full mt-4" onClick={() => (window.location.href = "/provider/me")}>
          Manage my provider page
        </Button>
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#EAEAEA] rounded-xl p-8 text-center" data-testid="empty-customer">
      <Calendar size={28} weight="duotone" className="text-gold mx-auto mb-3" />
      <h3 className="font-semibold text-ink">No bookings yet</h3>
      <p className="text-sm text-[#6B6B6B] mt-1">Find a trainer, groomer, walker, or sitter to book.</p>
      <Button className="rounded-full mt-4 bg-ink hover:bg-crimson text-white" onClick={() => (window.location.href = "/find-pros")}>
        Find a pro
      </Button>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h2 className="font-spectral text-2xl tracking-tight mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function BookingCard({ b, role, onCancel, onComplete, onReview, onReschedule, onReport, onViewReport, onPassport, compact, showSeriesActions, onPauseSeries, onResumeSeries, onCancelSeries }) {
  const badge = STATUS_BADGE[b.status] || STATUS_BADGE.confirmed;
  const isProvider = role === "provider";
  const seriesStatus = b.series_status; // optional — server may attach
  const isPaused = seriesStatus === "paused";
  return (
    <div className="bg-white border border-[#EAEAEA] rounded-xl p-4" data-testid={`booking-${b.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-ink">{b.service_name}</p>
            <span className={`text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 border ${badge.tone}`}>
              {badge.label}
            </span>
            {b.series_id && (
              <span className="text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-cream border border-gold/40 text-ink" data-testid={`series-badge-${b.id}`}>
                Session {(b.series_index ?? 0) + 1}/{b.series_total}
              </span>
            )}
            {(b.virtual || b.room_url) && (
              <span className="text-[10px] uppercase tracking-wider font-bold rounded-full px-2 py-0.5 bg-[#0E0E0E] text-white inline-flex items-center gap-1" data-testid={`virtual-badge-${b.id}`}>
                <VideoCamera size={10} weight="fill" /> Video session
              </span>
            )}
          </div>
          {b.room_url && b.status === "confirmed" && (
            <a
              href={b.room_url}
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white text-[12px] font-semibold px-4 py-1.5 transition-colors"
              data-testid={`booking-join-video-${b.id}`}
            >
              <VideoCamera size={13} weight="duotone" /> Join video call
            </a>
          )}
          <p className="text-[12.5px] text-[#6B6B6B] mt-1 flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1"><Clock size={12} weight="duotone" /> {prettyDateTime(b.scheduled_at)}</span>
            <span className="inline-flex items-center gap-1"><CurrencyDollar size={12} weight="duotone" /> ${(b.customer_total ?? b.amount)?.toFixed(2)}{b.buyer_protection_fee ? <span className="text-[10.5px] text-[#9A9A9A] ml-0.5">incl ${b.buyer_protection_fee.toFixed(2)} protection</span> : null}</span>
            {isProvider && Number.isFinite(b.amount - b.platform_fee) && (
              <span className="inline-flex items-center gap-1"><Receipt size={12} weight="duotone" /> You net ${(b.amount - b.platform_fee).toFixed(2)}</span>
            )}
          </p>
          {b.customer_notes && !compact && (
            <p className="text-[12.5px] text-[#6B6B6B] mt-2 italic">"{b.customer_notes}"</p>
          )}
          {/* Field Host bookings: the exact field address is revealed to
              the guest only once payment is confirmed. */}
          {!isProvider && b.field_exact_address && (
            <p className="text-[12.5px] text-[#2E6B3E] mt-2 inline-flex items-start gap-1.5 font-semibold bg-[#F2F8F3] border border-[#2E6B3E]/25 rounded-lg px-3 py-2" data-testid={`field-address-${b.id}`}>
              <MapPin size={13} weight="fill" className="mt-0.5 shrink-0" />
              <span>
                Field address: {b.field_exact_address}
                <a
                  href={`https://maps.google.com/?q=${encodeURIComponent(b.field_exact_address)}`}
                  target="_blank"
                  rel="noreferrer"
                  className="underline ml-1.5"
                  data-testid={`field-address-map-${b.id}`}
                >
                  Directions →
                </a>
              </span>
            </p>
          )}
          {b.status === "cancelled" && b.refund_amount != null && (
            <p className="text-[12px] text-[#6B6B6B] mt-1">
              Refund: ${b.refund_amount.toFixed(2)} ({b.cancelled_by})
            </p>
          )}
          {/* Buyer's own refund-policy acknowledgement receipt — proves
              they ticked the "no refunds, no mediation" click-wrap at
              checkout. Quietly informational; only renders to the buyer
              and only when the ack exists on the row. */}
          {!isProvider && b.legal_acks?.refund_policy_for_service_bookings?.signed_at && (
            <p
              className="text-[10.5px] text-[#0F8043] mt-2 inline-flex items-center gap-1 font-semibold"
              data-testid={`refund-ack-pill-${b.id}`}
              title={`Acknowledged v${b.legal_acks.refund_policy_for_service_bookings.version} at ${new Date(b.legal_acks.refund_policy_for_service_bookings.signed_at).toLocaleString()}`}
            >
              <ShieldCheck size={11} weight="fill" />
              Refund policy acknowledged · v{b.legal_acks.refund_policy_for_service_bookings.version}
              <a
                href="/refund#service-provider-bookings"
                target="_blank"
                rel="noreferrer"
                className="underline ml-1 hover:text-ink"
                onClick={(e) => e.stopPropagation()}
              >
                view
              </a>
            </p>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {!compact && b.status === "confirmed" && isProvider && (
            <Button size="sm" variant="outline" className="rounded-full" onClick={onComplete} data-testid={`complete-${b.id}`}>
              <CheckCircle size={13} weight="duotone" className="mr-1" /> Mark complete
            </Button>
          )}
          {!compact && onReschedule && ["pending_payment", "confirmed", "scheduled_pending"].includes(b.status) && (
            <Button size="sm" variant="outline" className="rounded-full" onClick={onReschedule} data-testid={`reschedule-${b.id}`}>
              <ArrowsClockwise size={13} weight="duotone" className="mr-1" /> Reschedule
            </Button>
          )}
          {!compact && ["pending_payment", "confirmed", "scheduled_pending", "payment_failed"].includes(b.status) && (
            <Button size="sm" variant="outline" className="rounded-full text-crimson border-crimson/40 hover:bg-rose-50" onClick={onCancel} data-testid={`cancel-${b.id}`}>
              <XCircle size={13} weight="duotone" className="mr-1" /> Cancel
            </Button>
          )}
          {onReview && (
            <Button size="sm" className="rounded-full bg-amber-400 hover:bg-amber-500 text-ink font-semibold" onClick={onReview} data-testid={`leave-review-${b.id}`}>
              <Star size={13} weight="fill" className="mr-1" /> Leave review
            </Button>
          )}
          {onPassport && (
            <Button size="sm" variant="outline" className="rounded-full" onClick={onPassport} data-testid={`view-passport-${b.id}`}>
              <PawPrint size={13} weight="duotone" className="mr-1" /> Care passport
            </Button>
          )}
          {onReport && (
            <Button size="sm" variant="outline" className="rounded-full border-gold text-ink hover:bg-cream" onClick={onReport} data-testid={`groom-report-${b.id}`}>
              <Scissors size={13} weight="duotone" className="mr-1" /> {b.groom_report ? "Edit report card" : "Send report card"}
            </Button>
          )}
          {onViewReport && (
            <Button size="sm" className="rounded-full bg-gold-gradient text-ink font-semibold" onClick={onViewReport} data-testid={`view-groom-report-${b.id}`}>
              <Scissors size={13} weight="duotone" className="mr-1" /> Report card
            </Button>
          )}
        </div>
      </div>
      {role === "customer" && b.rebook_due_at && b.status === "completed" && (
        <RebookBanner b={b} />
      )}
      {/* Live-walk tracking — slot in for walking + sitting bookings on
          the day of (or during) the appointment. Provider sees the
          start/end control; buyer sees a consent toggle + map link.
          Gated behind GPS_LIVE_WALK_ENABLED — feature is built but
          parked while liability posture is finalized. */}
      {GPS_LIVE_WALK_ENABLED && !compact && b.status === "confirmed" && (
        isProvider ? (
          <div className="mt-4" data-testid={`walker-tracking-${b.id}`}>
            <WalkerLiveTracking booking={b} />
          </div>
        ) : (
          <BuyerLiveWalkBadge
            bookingId={b.id}
            initialConsent={Boolean(b.live_walk_consent)}
          />
        )
      )}
      {showSeriesActions && (
        <div
          className="mt-3 pt-3 border-t border-[#F0F0F0] flex items-center justify-between gap-3 flex-wrap"
          data-testid={`series-actions-${b.series_id}`}
        >          <div className="text-[12px] text-[#6B6B6B]">
            <span className="font-semibold text-ink">Recurring series</span> · {b.series_total} sessions total · auto-charges 24h before each visit
          </div>
          <div className="flex gap-2 flex-wrap">
            {isPaused ? (
              <Button
                size="sm"
                variant="outline"
                className="rounded-full"
                onClick={onResumeSeries}
                data-testid={`series-resume-${b.series_id}`}
              >
                Resume series
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                className="rounded-full"
                onClick={onPauseSeries}
                data-testid={`series-pause-${b.series_id}`}
              >
                Pause series
              </Button>
            )}
            <Button
              size="sm"
              variant="outline"
              className="rounded-full text-crimson border-crimson/40 hover:bg-rose-50"
              onClick={onCancelSeries}
              data-testid={`series-cancel-${b.series_id}`}
            >
              Cancel &amp; refund future sessions
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
